import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, TextField, MenuItem,
  Alert, Avatar, Divider, Grid, Switch, FormControlLabel, Chip,
  CircularProgress, Snackbar, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Business, Schedule, BeachAccess, Payment, Notifications,
  Palette, Save, Settings,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const INDUSTRIES = [
  'Technology', 'Blockchain / Identity Technology', 'Finance & Banking',
  'Healthcare', 'Retail', 'Manufacturing', 'Education', 'Consulting',
  'Media & Entertainment', 'Real Estate', 'Legal', 'Other',
];
const CURRENCIES = ['HKD', 'USD', 'EUR', 'GBP', 'SGD', 'CNY', 'JPY', 'AUD'];
const TIMEZONES = [
  'Asia/Hong_Kong', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo',
  'Europe/London', 'America/New_York', 'America/Los_Angeles', 'UTC',
];

interface Settings {
  companyName: string;
  legalName?: string;
  registrationNumber?: string;
  taxId?: string;
  address?: string;
  city?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  logoUrl?: string;
  industry?: string;
  defaultCurrency: string;
  timezone: string;
  defaultLanguage: string;
  fiscalYearStartMonth: number;
  fiscalYearEndMonth: number;
  workdayStart: string;
  workdayEnd: string;
  lunchBreakMinutes: number;
  standardHoursPerDay: number;
  standardHoursPerWeek: number;
  lateGraceMinutes: number;
  workingDays: string[];
  defaultAnnualLeaveDays: number;
  defaultSickLeaveDays: number;
  maxCarryOverDays: number;
  requireLeaveApproval: boolean;
  minLeaveAdvanceNoticeDays: number;
  payrollCutoffDay: number;
  payrollPayDay: number;
  payFrequency: string;
  defaultProbationMonths: number;
  notifyLeaveApprovals: boolean;
  notifyBirthdays: boolean;
  notifyWorkAnniversaries: boolean;
  notifyExpiringDocuments: boolean;
  documentExpiryAlertDays: number;
  primaryColor?: string;
  secondaryColor?: string;
}

function SectionHeader({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
      <Avatar sx={{ bgcolor: '#C08A2E20', color: '#C08A2E' }}>{icon}</Avatar>
      <Box>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </Box>
    </Box>
  );
}

export default function CompanySettings() {
  const { user } = useAuth();
  const isHRAdmin = (user as any)?.role === 'hr_admin';

  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [draft, setDraft] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    apiFetch('/api/company-settings')
      .then(d => { setSettings(d); setDraft(d); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const set = (key: keyof Settings, val: any) => {
    setDraft(p => p ? { ...p, [key]: val } : p);
    setDirty(true);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const updated = await apiFetch('/api/company-settings', {
        method: 'PATCH',
        body: JSON.stringify(draft),
      });
      setSettings(updated);
      setDraft(updated);
      setDirty(false);
      setSuccess(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day: string) => {
    if (!draft) return;
    const days = draft.workingDays.includes(day)
      ? draft.workingDays.filter(d => d !== day)
      : [...draft.workingDays, day];
    set('workingDays', days);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!draft) return null;

  const tabs = [
    { label: 'Company', icon: <Business fontSize="small" /> },
    { label: 'Working Hours', icon: <Schedule fontSize="small" /> },
    { label: 'Leave', icon: <BeachAccess fontSize="small" /> },
    { label: 'Payroll', icon: <Payment fontSize="small" /> },
    { label: 'Notifications', icon: <Notifications fontSize="small" /> },
    { label: 'Branding', icon: <Palette fontSize="small" /> },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#C08A2E', width: 48, height: 48 }}><Settings /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Company Settings</Typography>
            <Typography variant="body2" color="text.secondary">System-wide configuration for your HRIS</Typography>
          </Box>
        </Box>
        {isHRAdmin && (
          <Button
            variant="contained" startIcon={<Save />} onClick={save}
            disabled={!dirty || saving}
            sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#9E6F22' } }}
          >
            {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
          </Button>
        )}
      </Box>

      {!isHRAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>These settings are read-only. Only HR Admin can make changes.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '1px solid #eee', px: 1 }}>
          {tabs.map((t, i) => (
            <Tab key={i} label={t.label} icon={t.icon} iconPosition="start"
              sx={{ minHeight: 48, textTransform: 'none' }} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* ── Company Profile ── */}
          {tab === 0 && (
            <>
              <SectionHeader icon={<Business />} title="Company Profile" subtitle="Basic information about your organisation" />
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Company Name" value={draft.companyName}
                    onChange={e => set('companyName', e.target.value)} disabled={!isHRAdmin} required />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Legal / Registered Name" value={draft.legalName || ''}
                    onChange={e => set('legalName', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Business Registration Number" value={draft.registrationNumber || ''}
                    onChange={e => set('registrationNumber', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Tax ID" value={draft.taxId || ''}
                    onChange={e => set('taxId', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={12}>
                  <TextField fullWidth label="Registered Address" value={draft.address || ''}
                    onChange={e => set('address', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="City" value={draft.city || ''}
                    onChange={e => set('city', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Country" value={draft.country || ''}
                    onChange={e => set('country', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Phone" value={draft.phone || ''}
                    onChange={e => set('phone', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="HR Email" value={draft.email || ''}
                    onChange={e => set('email', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Website" value={draft.website || ''}
                    onChange={e => set('website', e.target.value)} disabled={!isHRAdmin} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select fullWidth label="Industry" value={draft.industry || ''}
                    onChange={e => set('industry', e.target.value)} disabled={!isHRAdmin}>
                    {INDUSTRIES.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Logo URL" value={draft.logoUrl || ''}
                    onChange={e => set('logoUrl', e.target.value)} disabled={!isHRAdmin}
                    placeholder="https://example.com/logo.png" />
                </Grid>

                <Grid size={12}><Divider><Typography variant="caption" color="text.secondary">Localisation</Typography></Divider></Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select fullWidth label="Default Currency" value={draft.defaultCurrency}
                    onChange={e => set('defaultCurrency', e.target.value)} disabled={!isHRAdmin}>
                    {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select fullWidth label="Timezone" value={draft.timezone}
                    onChange={e => set('timezone', e.target.value)} disabled={!isHRAdmin}>
                    {TIMEZONES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select fullWidth label="Fiscal Year Start" value={draft.fiscalYearStartMonth}
                    onChange={e => set('fiscalYearStartMonth', Number(e.target.value))} disabled={!isHRAdmin}
                    helperText="HK standard: April">
                    {MONTHS.slice(1).map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
            </>
          )}

          {/* ── Working Hours ── */}
          {tab === 1 && (
            <>
              <SectionHeader icon={<Schedule />} title="Working Hours & Schedule" subtitle="Define your standard working week" />
              <Grid container spacing={2.5}>
                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>Working Days</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {DAYS.map(day => (
                      <Chip
                        key={day}
                        label={day}
                        onClick={isHRAdmin ? () => toggleDay(day) : undefined}
                        color={draft.workingDays.includes(day) ? 'primary' : 'default'}
                        variant={draft.workingDays.includes(day) ? 'filled' : 'outlined'}
                        sx={{ cursor: isHRAdmin ? 'pointer' : 'default', fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField fullWidth label="Day Start" type="time" value={draft.workdayStart}
                    onChange={e => set('workdayStart', e.target.value)} disabled={!isHRAdmin}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField fullWidth label="Day End" type="time" value={draft.workdayEnd}
                    onChange={e => set('workdayEnd', e.target.value)} disabled={!isHRAdmin}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField fullWidth label="Lunch Break (mins)" type="number"
                    value={draft.lunchBreakMinutes}
                    onChange={e => set('lunchBreakMinutes', Number(e.target.value))}
                    disabled={!isHRAdmin} inputProps={{ min: 0, max: 120 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField fullWidth label="Late Grace Period (mins)" type="number"
                    value={draft.lateGraceMinutes}
                    onChange={e => set('lateGraceMinutes', Number(e.target.value))}
                    disabled={!isHRAdmin}
                    helperText="Minutes after start before marking late"
                    inputProps={{ min: 0, max: 60 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Standard Hours / Day" type="number"
                    value={draft.standardHoursPerDay}
                    onChange={e => set('standardHoursPerDay', Number(e.target.value))}
                    disabled={!isHRAdmin} inputProps={{ min: 1, max: 24 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Standard Hours / Week" type="number"
                    value={draft.standardHoursPerWeek}
                    onChange={e => set('standardHoursPerWeek', Number(e.target.value))}
                    disabled={!isHRAdmin} inputProps={{ min: 1, max: 80 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Default Probation (months)" type="number"
                    value={draft.defaultProbationMonths}
                    onChange={e => set('defaultProbationMonths', Number(e.target.value))}
                    disabled={!isHRAdmin} inputProps={{ min: 1, max: 12 }} />
                </Grid>
              </Grid>
            </>
          )}

          {/* ── Leave ── */}
          {tab === 2 && (
            <>
              <SectionHeader icon={<BeachAccess />} title="Leave Policy Defaults" subtitle="Default settings applied to new employees" />
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Default Annual Leave (days)" type="number"
                    value={draft.defaultAnnualLeaveDays}
                    onChange={e => set('defaultAnnualLeaveDays', Number(e.target.value))}
                    disabled={!isHRAdmin} inputProps={{ min: 0, max: 365 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Default Sick Leave (days)" type="number"
                    value={draft.defaultSickLeaveDays}
                    onChange={e => set('defaultSickLeaveDays', Number(e.target.value))}
                    disabled={!isHRAdmin} inputProps={{ min: 0, max: 365 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Max Carry-Over Days" type="number"
                    value={draft.maxCarryOverDays}
                    onChange={e => set('maxCarryOverDays', Number(e.target.value))}
                    disabled={!isHRAdmin} inputProps={{ min: 0 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Min Advance Notice (days)" type="number"
                    value={draft.minLeaveAdvanceNoticeDays}
                    onChange={e => set('minLeaveAdvanceNoticeDays', Number(e.target.value))}
                    disabled={!isHRAdmin}
                    helperText="How far in advance leave must be requested"
                    inputProps={{ min: 0 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draft.requireLeaveApproval}
                          onChange={e => set('requireLeaveApproval', e.target.checked)}
                          disabled={!isHRAdmin}
                        />
                      }
                      label="Require Manager Approval for Leave Requests"
                    />
                  </Box>
                </Grid>
              </Grid>
            </>
          )}

          {/* ── Payroll ── */}
          {tab === 3 && (
            <>
              <SectionHeader icon={<Payment />} title="Payroll Settings" subtitle="Payroll cycle and payment configuration" />
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select fullWidth label="Pay Frequency" value={draft.payFrequency}
                    onChange={e => set('payFrequency', e.target.value)} disabled={!isHRAdmin}>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="biweekly">Bi-weekly</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Payroll Cutoff Day" type="number"
                    value={draft.payrollCutoffDay}
                    onChange={e => set('payrollCutoffDay', Number(e.target.value))}
                    disabled={!isHRAdmin}
                    helperText="Day of the month payroll closes"
                    inputProps={{ min: 1, max: 31 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Pay Day" type="number"
                    value={draft.payrollPayDay}
                    onChange={e => set('payrollPayDay', Number(e.target.value))}
                    disabled={!isHRAdmin}
                    helperText="Day of the following month salaries are paid"
                    inputProps={{ min: 1, max: 31 }} />
                </Grid>
                <Grid size={12}>
                  <Alert severity="info">
                    <strong>Current cycle:</strong> Cutoff on the {draft.payrollCutoffDay}th · Salaries paid on the {draft.payrollPayDay}th of the following month · {draft.payFrequency.charAt(0).toUpperCase() + draft.payFrequency.slice(1)}
                  </Alert>
                </Grid>
              </Grid>
            </>
          )}

          {/* ── Notifications ── */}
          {tab === 4 && (
            <>
              <SectionHeader icon={<Notifications />} title="Notification Settings" subtitle="Configure automatic HR alerts and reminders" />
              <Grid container spacing={1}>
                {[
                  { key: 'notifyLeaveApprovals', label: 'Leave approval / rejection notifications', desc: 'Notify employees when their leave is approved or rejected' },
                  { key: 'notifyBirthdays', label: 'Birthday reminders', desc: 'Remind HR and managers about upcoming employee birthdays' },
                  { key: 'notifyWorkAnniversaries', label: 'Work anniversary alerts', desc: 'Notify HR of milestone work anniversaries (1, 3, 5, 10 years…)' },
                  { key: 'notifyExpiringDocuments', label: 'Expiring document alerts', desc: 'Warn HR when visas, certifications, or contracts are expiring soon' },
                ].map(({ key, label, desc }) => (
                  <Grid size={12} key={key}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={(draft as any)[key]}
                            onChange={e => set(key as any, e.target.checked)}
                            disabled={!isHRAdmin}
                          />
                        }
                        label={<Box><Typography variant="body2" fontWeight={600}>{label}</Typography><Typography variant="caption" color="text.secondary">{desc}</Typography></Box>}
                      />
                    </Paper>
                  </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Document Expiry Alert Window (days)" type="number"
                    value={draft.documentExpiryAlertDays}
                    onChange={e => set('documentExpiryAlertDays', Number(e.target.value))}
                    disabled={!isHRAdmin}
                    helperText="How many days before expiry to trigger alerts"
                    inputProps={{ min: 7, max: 180 }}
                    sx={{ mt: 1 }} />
                </Grid>
              </Grid>
            </>
          )}

          {/* ── Branding ── */}
          {tab === 5 && (
            <>
              <SectionHeader icon={<Palette />} title="Branding" subtitle="Customise the look of your HRIS" />
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Primary Brand Colour" value={draft.primaryColor || ''}
                    onChange={e => set('primaryColor', e.target.value)} disabled={!isHRAdmin}
                    placeholder="#C08A2E" helperText="Used for buttons and key UI elements" />
                  {draft.primaryColor && (
                    <Box sx={{ mt: 1, height: 32, borderRadius: 1, bgcolor: draft.primaryColor }} />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Secondary Brand Colour" value={draft.secondaryColor || ''}
                    onChange={e => set('secondaryColor', e.target.value)} disabled={!isHRAdmin}
                    placeholder="#9E6F22" helperText="Used for hover states and accents" />
                  {draft.secondaryColor && (
                    <Box sx={{ mt: 1, height: 32, borderRadius: 1, bgcolor: draft.secondaryColor }} />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Logo URL" value={draft.logoUrl || ''}
                    onChange={e => set('logoUrl', e.target.value)} disabled={!isHRAdmin}
                    placeholder="https://example.com/logo.png" />
                  {draft.logoUrl && (
                    <Box sx={{ mt: 1 }}>
                      <img src={draft.logoUrl} alt="Company logo preview" style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain' }} />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Paper>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}
        message="✅ Settings saved successfully" />
    </Box>
  );
}
